import React, { MouseEvent, useContext, useId, useMemo } from "react";
import { Title } from "../components/Title";
import { asIdSet } from "../components/utils";
import { useIsAwr, useIsGuest } from "../login/AuthContext";
import { Account, getAccountLink, getAdsLink, getBillingLink, getFranchise, getGoogleLink, getLeadsProjection, goalClass, goalTextClass, leadCost, monthGoal, suggestedBudget } from "../models/Account";
import { frequency, FullReportEntry, gap, missingInfoDayCount, percentage, ReportEntry } from "../models/FullReportEntry";
import { Column, Table } from "../table/Table";
import { AccountTags } from "../tags/tags";
import { bigNumber, decimal, formatMoney } from "../utils/money-utils";
import { BatchActions } from "./AccountBatchModal";
import { BalanceTag } from "./AccountComponents";
import { AccountContext, useGoogleMode, usePastRender } from "./AccountContext";
import { AccountDetail } from "./AccountDetail";
import AccountFilterBox from "./AccountFilter";
import { gapStyle, leadGoalStyle, totalCostStyle } from "./table/column-styles";
import { ColumnSettingsButton, filterHiddenColumns } from "./table/hidden-columns";
import { pastMonth } from "./table/past-month-cell";
import { SortHeader } from "./table/table-headers";

function GapRender({account}:{account:ReportEntry}){
    const g = useMemo(() => gap(account), [account])
    return g >= 0 ? <><i className="fa fa-arrow-up me-1" style={{fontSize: '0.7em', verticalAlign:"20%"}}></i> {bigNumber(g)}</> : 
    <><i className="fa fa-arrow-down me-1" style={{fontSize: '0.7em', verticalAlign:"20%"}}></i> {bigNumber(Math.abs(g))}</>
}

function PastMonthLeads({account}:{account:FullReportEntry}){
    const diff = account.nLeads - account.pastMonthSameDay.leads
    const icon  = diff > 0 ? 'fa fa-arrow-up' : 'fa fa-arrow-down';
    const ratio = account.nLeads ? diff / account.nLeads : 100

    return <><i style={{fontSize: '0.7em', verticalAlign:"20%"}} className={icon}></i> {Math.abs(diff)} ({decimal(ratio * 100)}%) </>
}

function PastMonthCost({account}:{account:FullReportEntry}){
    const diff = account.totalCost - account.pastMonthSameDay.cost
    const icon  = diff > 0 ? 'fa fa-arrow-up' : 'fa fa-arrow-down';
    const ratio = account.totalCost ? diff / account.totalCost : 100

    return <><i style={{fontSize: '0.7em', verticalAlign:"20%"}} className={icon}></i> {formatMoney(Math.abs(diff))} ({decimal(ratio * 100)}%) </>
}

function AccountNameRender({account}:{account:FullReportEntry}){
    const {customGoalMode} = useContext(AccountContext);
    return <>{account.name}{customGoalMode && account.goalSpread != 0  && <i className="ms-2 fa fa-circle text-warning" style={{fontSize:".5em"}}></i>}</>;
}

export function PastMonthLeadsCell({account}:{account:FullReportEntry}){
    const diff = account.nLeads - account.pastMonthSameDay.leads
    const icon  = diff > 0 ? 'fa fa-arrow-up' : 'fa fa-arrow-down';
    const ratio = account.nLeads ? diff / account.nLeads : 1

    return <><i className={icon} style={{fontSize: "0.7em", verticalAlign: "20%"}}></i> {Math.abs(diff)} <small className="text-muted">({decimal(ratio * 100)}%)</small> </>
}

export function PastMonthYesterdayLeadsCell({account}:{account:FullReportEntry}){
    const diff = account.yesterDayInfo.leads - account.pastMonthYesterDayInfo.leads
    const icon  = diff > 0 ? 'fa fa-arrow-up' : 'fa fa-arrow-down';
    const ratio = account.yesterDayInfo.leads ? diff / account.yesterDayInfo.leads : 1

    return <><i className={icon} style={{fontSize: "0.7em", verticalAlign: "20%"}}></i> {Math.abs(diff)} <small className="text-muted" title={`${account.yesterDayInfo.leads} : ${account.pastMonthYesterDayInfo.leads}`}>({decimal(ratio * 100)}%)</small> </>
}

function buildCols():Column<FullReportEntry>[]{
    const awr = useIsAwr()
    const googleMode = (document.querySelector('#root') as any).dataset.mode == 'google';

    return [
        {id: "franchise", label: "Franquia", render: account => getFranchise(account)},
        {id: "name", label: <SortHeader sortId="nome-da-conta">Conta</SortHeader>, render: account => <AccountNameRender account={account}/>},
        {id: "tags", label: "Tags", render: account => <AccountTags account={account} mode="short"/>},
        {id: "manager", label: <SortHeader sortId="gestor">Gestor</SortHeader>, render: account => account.manager},
        {id: "consultant", label: <SortHeader sortId="consultor">Consultor</SortHeader>, render: account => account.consultant},
        {id: "status", label: <SortHeader sortId="status-da-conta">St.</SortHeader>, render: account => <StatusRender account={account} /> },
        {id: "balance", label:  <SortHeader sortId="saldo">Saldo</SortHeader>, render: account => <BalanceTag account={account} />},
        {id: "reach", label: "Alcance", render: pastMonth(account => bigNumber(account.reach))},
        {id: "frequency", label: "Frequência", render: pastMonth(account => decimal(frequency(account)))},
        {id: "missingDays", label: <SortHeader sortId="dias-faltando">Dias<br /><small>Faltando</small></SortHeader>, render: account => account.missingDays || ""},
        //{id: "leadGain", label: <SortHeader sortId="leads-mes-passado">Lead<br /><small>Diferença</small></SortHeader>, render: account => <PastMonthLeadsCell account={account} />, },
        {id: "leadGain", label: <SortHeader sortId="leads-mes-passado">Lead<br /><small>Diferença</small></SortHeader>, render: account => <PastMonthYesterdayLeadsCell account={account} />, },
        
        
        
        {id: "gap", label: <SortHeader sortId="gap-atual">Gap<br /><small>atual</small></SortHeader>, render: pastMonth(account => <GapRender account={account} /> ),colGroup: gapStyle},
        {id: "leadCost", label: <SortHeader sortId="custo-por-lead">Custo<br /><small>por lead</small></SortHeader>, render: pastMonth(account => formatMoney(leadCost(account))), colGroup: leadGoalStyle},
        {id: "leads", label: <SortHeader sortId="leads-no-mes">Leads<br /><small>Atual / Meta</small></SortHeader>, render: pastMonth(account => <>{account.nLeads} / {account.goal}</>), colGroup: leadGoalStyle},
        {id: "monthGoal", label: <SortHeader sortId="meta-geral">Meta Leads<br /><small>Geral</small></SortHeader>, render: pastMonth(account => <span className={goalTextClass(account)}>{decimal(monthGoal(account) * 100)}%</span>)},
        {id: "goalA", label: <SortHeader sortId="meta-proporcional">Meta Leads<br /><small>Proporcional</small></SortHeader>, render: pastMonth(account => <span className={goalTextClass(account)}>{decimal(account.goalRatio * 100)}%</span>)},
        {id: "budget", label: <SortHeader sortId="orcamento">Orçamento</SortHeader>, render: pastMonth(account => formatMoney(account.budget))},
        
        ... (awr && !googleMode) ? [
        {id: "googleBudget", label: <SortHeader sortId="orcamento-google">Orçamento Google</SortHeader>, render: pastMonth(account => account.info?.googleBudget ? formatMoney(account.info?.googleBudget) : '')},
        {id: "totalBudget", label: <SortHeader sortId="orcamento-total">Orçamento Total</SortHeader>, render: pastMonth(account => formatMoney(account.budget + (account.info?.googleBudget || 0)))},
        ] : [],
        
        {id: "totalCost", label: "Investimento", render: pastMonth(account => formatMoney(account.totalCost)), colGroup: totalCostStyle},
        {id: "finalCost", label: <>Investimento<br /><small>Total</small></>, render: pastMonth(account => formatMoney(account.totalCost + (account.info?.filteredCampaigns?.cost || 0)))},
        {id: "suggestedBudget", label: <>Investimento<br /><small>Necessário</small></>, render: account => formatMoney(suggestedBudget(account))},
        {id: "overrun", label: <SortHeader sortId="investimento-orcamento">Investimento<br /><small>/ orçamento</small></SortHeader>, render: pastMonth(account => <span className={''}>{decimal(account.overrun * 100)}%</span>), colGroup: totalCostStyle},
        {id: "projection", label: <>Projeção<br /><small>de leads</small></>, render: account => Math.round(getLeadsProjection(account.nLeads))},
        {id: "leadsComp", label: <SortHeader sortId="comparativo-leads">Comparativo<br /><small>de leads</small></SortHeader>, render: account => <PastMonthLeads account={account} />},
        {id: "costComp", label: <SortHeader sortId="comparativo-investimento">Comparativo<br /><small>Investimento</small></SortHeader>, render: account => <PastMonthCost account={account} />},
        {id: "trend", label: <SortHeader sortId="tendencia">Tend.</SortHeader>, render: account => <TrendRender account={account} /> }
    ]
}

function googleCols():Column<FullReportEntry>[]{
    const r = buildCols().filter(c => !['balance', 'reach', 'frequency'].includes(c.id));
    r.splice(6, 0, 
        {id: "score", label: <SortHeader sortId="otimizacao">Otimização</SortHeader>, render: pastMonth(account => decimal((account?.score || 0) * 100) + "%")},
        {id: "clicks", label: <SortHeader sortId="clicks">Clicks</SortHeader>, render: pastMonth(account => account.clicks)},
    );
    return r;
}

function buildSimpleCols():Column<FullReportEntry>[]{
    return [
        {id: "name", label: "Conta", render: account => <AccountNameRender account={account}/>},
        {id: "leadsS", label: <>Leads<br /><small>no mês</small></>, render: pastMonth(account => bigNumber(account.nLeads))},
        {id: "reach", label: "Alcance", render: pastMonth(account => bigNumber(account.reach))},
        {id: "totalCostS", label: "Investimento", render: pastMonth(account => formatMoney(account.totalCost))},
        {id: "finalCost", label: <>Investimento<br /><small>Total</small></>, render: pastMonth(account => formatMoney(account.totalCost + (account.info?.filteredCampaigns?.cost || 0)))},
        {id: "a", label: "Impressões", render: pastMonth(account => bigNumber(account.impressions))},
    ]
}

function hideFilteredCampaigns(fn: () => Column<FullReportEntry>[]){
    return () => {
        const cols = fn();
        return cols.filter(a => !['finalCost'].includes(a.id))
    }
}

export function StatusRender({account}:{account:Account}){
    function getClass(){
        switch(account.status){
            case 0: return 'fa fa-check text-success';
            case 3: return 'fa fa-exclamation-triangle text-warning';
            default: return 'fa fa-exclamation-triangle text-danger';
        }
    }
    return  <i className={getClass()}></i>
}

export function TrendRender({account}:{account:FullReportEntry}){
    function getClass(){
        switch(account.trend){
            case 'up': return 'fa fa-arrow-trend-up text-success';
            case 'down': return 'fa fa-arrow-trend-down text-danger';
            case 'missing': return 'fa fa-minus text-danger';
            default: return 'fa fa-minus text-info';
        }
    }

    return <i className={getClass()}></i>
    
}

function PastMonthToogle(){
    const pastRender = usePastRender();
    const {dispatch} = useContext(AccountContext);
    const id = useId()
    return <div className="form-check ms-3">
        <input className="form-check-input" type="checkbox" checked={pastRender || false} onChange={() => dispatch({type: "setPastRender", payload: !pastRender})} id={id} />
        <label className="form-check-label" htmlFor={id}>
            Mês Passado
        </label>
    </div>
}

function columnFunction(simple:boolean, isGoogleMode:boolean, accounts:FullReportEntry[]){
    const hasFilteredCampaigns = accounts.find(a => a.info?.filteredCampaigns)
    if(!hasFilteredCampaigns){
        if(simple) return hideFilteredCampaigns(buildSimpleCols)
        return hideFilteredCampaigns(buildCols)
    }

    if(simple) return buildSimpleCols
    return isGoogleMode ? googleCols : buildCols
}

export function AccountTableView({accounts}:{accounts:FullReportEntry[]}){
    const {dispatch, account, hiddenColumns, tableMode} = useContext(AccountContext);
    const isGuest = useIsGuest();
    const isGoogleMode = useGoogleMode();

    const columns = useMemo(columnFunction((tableMode === 'simple' || isGuest), isGoogleMode, accounts), [tableMode, accounts]);
    const visibleColumns = useMemo(()=> filterHiddenColumns(columns, hiddenColumns), [columns, hiddenColumns])

    function onClick(account:FullReportEntry, e:MouseEvent){
        if((e.ctrlKey || e.metaKey) && e.shiftKey){
            open(getBillingLink(account), account.metaId);
            return;
        }

        if(e.ctrlKey || e.metaKey){
            open(getAccountLink(account), account.metaId);
            return;
        }

        if(e.altKey){
            open(getAdsLink(account), account.metaId);
            return;
        }

        if(e.shiftKey){
            open(getGoogleLink(account), account.metaId);
            return;
        }

        dispatch({type:"selectAccount", payload: account});
    }

    return <>
        <BatchActions />
        <div className={account == null ? "p-3 pb-0 px-2" : "p-3 px-2"}>
            <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                    <Title detailRef={null} />
                </div>
                <ColumnSettingsButton columns={columns} />
            </div>
            {account != null && <div className="mx-2"><AccountDetail /></div>}
            {account == null && <>
                <AccountFilterBox /> 
                <div className="mb-1 d-flex">
                    <div>Exibindo {accounts.length} conta{accounts.length == 1 ? '' : 's'}.</div>
                    <PastMonthToogle />
                </div>  
                <div className="table-view">
                    <Table data={accounts} columns={visibleColumns} onClick={onClick} />
                </div>
            </>}
        </div>
    </>
}